import React, { useEffect, useMemo, useRef } from 'react';

export type InteractiveDNAProps = {
  width?: number;
  height?: number;
  aspectRatio?: number;
  background?: string;
  colour?: string;

  dotColorA?: string;
  dotColorB?: string;
  dotSize?: number;
  amplitudeBase?: number;
  turns?: number;
  rotationDeg?: number;

  rungs?: number;
  rungColor?: string;
  rungDotCount?: number;
  rungDotScale?: number;

  breakIntensity?: number;
  springK?: number;
  damping?: number;

  bokehEnabled?: boolean;
  bokehCount?: number;
  bokehColor?: string;

  /** Horizontal placement of the helix axis in screen space */
  position?: any;
};

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

const posFrac = (p: 'left' | 'center' | 'right') =>
  p === 'left' ? 0.25 : p === 'right' ? 0.75 : 0.5;

const InteractiveDNA: React.FC<InteractiveDNAProps> = ({
  width,
  height,
  aspectRatio = 3,
  background = 'transparent',
  colour,

  dotColorA = colour == 'light' ? '#68cbe2' : '#0052FF',
  dotColorB = colour == 'light' ? '#2eddc2' : '#2600BB',
  dotSize = 3,
  amplitudeBase = 0.19,
  turns = 2.5,
  rotationDeg = -8,

  rungs = 50,
  rungDotCount = 12,
  rungDotScale = 0.55,
  rungColor = colour == 'light' ? '#FFFFFF' : '#8a96bf',

  breakIntensity = 3,
  springK: springKProp = 0.0075,
  damping: dampingProp = 0.85,

  bokehEnabled = true,
  bokehCount = 48,
  bokehColor = '#ffffff',

  position = 'center',
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const mouseRef = useRef<{ x: number; y: number; active: boolean }>({
    x: 0,
    y: 0,
    active: false,
  });

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window))
      return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // ---------- sizing ----------
    const resize = (): void => {
      const parentW = width ?? (wrapper.clientWidth || 800);
      const parentH = height ?? (wrapper.clientHeight || 400);
      const parentAR = parentW / Math.max(1, parentH);

      let cssW: number, cssH: number;
      if (parentAR >= aspectRatio) {
        cssH = parentH;
        cssW = Math.min(parentW, cssH * aspectRatio);
      } else {
        cssW = parentW;
        cssH = Math.min(parentH, cssW / aspectRatio);
      }

      const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
      canvas.width = Math.floor(cssW * dpr);
      canvas.height = Math.floor(cssH * dpr);
      canvas.style.width = cssW + 'px';
      canvas.style.height = cssH + 'px';
      // Normalize to CSS pixels for drawing math
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const left = (parentW - cssW) / 2;
      const top = (parentH - cssH) / 2;
      canvas.style.position = 'absolute';
      canvas.style.left = left + 'px';
      canvas.style.top = top + 'px';
    };

    const cssW = () => canvas.clientWidth;
    const cssH = () => canvas.clientHeight;

    type Dot = {
      bx: number;
      by: number;
      x: number;
      y: number;
      vx: number;
      vy: number;
      strand: 0 | 1 | 2;
      depth: number;
      tFrac: number;
      tau?: number;
    };
    type Bokeh = {
      x: number;
      y: number;
      r: number;
      vx: number;
      vy: number;
      z: number;
    };
    let dots: Dot[] = [];
    let bokeh: Bokeh[] = [];

    const baseRepelRadiusFactor = 0.25;
    const baseRepelStrength = 0.85;
    const springK = springKProp;
    const damping = dampingProp;

    const rebuildDNA = (): void => {
      const W = cssW();
      const H = cssH();
      const amplitude = Math.min(W, H) * amplitudeBase;

      dots = [];
      const total = clamp(rungs ?? (dotSize === 3 ? 50 : 30), 20, 200);

      for (let i = 0; i < total; i++) {
        const t = i / (total - 1);
        const x = t * W; // LOCAL coordinates (no baked offset)
        const theta = t * turns * Math.PI * 2;
        const yCenter = H * 0.5;

        const depthA = Math.cos(theta);
        const yA = yCenter + amplitude * Math.sin(theta);
        dots.push({
          bx: x,
          by: yA,
          x,
          y: yA,
          vx: 0,
          vy: 0,
          strand: 0,
          depth: depthA,
          tFrac: t,
        });

        const depthB = Math.cos(theta + Math.PI);
        const yB = yCenter + amplitude * Math.sin(theta + Math.PI);
        dots.push({
          bx: x,
          by: yB,
          x,
          y: yB,
          vx: 0,
          vy: 0,
          strand: 1,
          depth: depthB,
          tFrac: t,
        });

        const count = clamp(Math.floor(rungDotCount), 0, 64);
        if (count > 0) {
          for (let k = 1; k <= count; k++) {
            const tau = k / (count + 1);
            const ry = yA * (1 - tau) + yB * tau;
            const rDepth = depthA * (1 - tau) + depthB * tau;
            dots.push({
              bx: x,
              by: ry,
              x,
              y: ry,
              vx: 0,
              vy: 0,
              strand: 2,
              depth: rDepth,
              tFrac: t,
              tau,
            });
          }
        }
      }
    };

    const rebuildBokeh = (): void => {
      if (!bokehEnabled) {
        bokeh = [];
        return;
      }
      const W = cssW();
      const H = cssH();
      const count = clamp(Math.floor(bokehCount), 0, 200);
      bokeh = new Array(count).fill(0).map(() => {
        const z = Math.random();
        const r = (0.001 + z * 0.004) * W;
        const speed = (0.05 + z * 0.25) * (Math.random() < 0.5 ? 1 : -1);
        const angle = Math.random() * Math.PI * 2;
        const vx = Math.cos(angle) * speed;
        const vy = Math.sin(angle) * speed;
        return {
          x: Math.random() * W,
          y: Math.random() * H,
          r,
          vx,
          vy,
          z,
        } as Bokeh;
      });
    };

    // init
    resize();
    rebuildDNA();
    rebuildBokeh();

    const handleResize = (): void => {
      resize();
      rebuildDNA();
      rebuildBokeh();
    };

    let ro: ResizeObserver | null = null;
    if (typeof window.ResizeObserver !== 'undefined') {
      ro = new window.ResizeObserver(() => handleResize());
      ro.observe(wrapper);
    }
    window.addEventListener('resize', handleResize);

    const angle = (rotationDeg * Math.PI) / 180;
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);

    // DRAW: 1) Translate(dx,0)  2) Rotate around canvas center
    // INVERSE(pointer): 1) Subtract dx  2) Un-rotate around canvas center
    const screenToLocal = (
      px: number,
      py: number
    ): { x: number; y: number } => {
      const W = cssW();
      const H = cssH();
      const cx = W * 0.5;
      const cy = H * 0.5;
      const dx = W * (posFrac(position) - 0.5);

      // ---- FIXED ORDER ----
      // 1) undo pre-rotation screen translation
      const sx = px - dx;
      const sy = py;

      // 2) undo rotation about center
      const rx = sx - cx;
      const ry = sy - cy;
      const lx = rx * cosA + ry * sinA + cx;
      const ly = -rx * sinA + ry * cosA + cy;

      return { x: lx, y: ly };
    };

    // Interaction
    const onMove = (e: MouseEvent | TouchEvent): void => {
      const rect = canvas.getBoundingClientRect();
      let sx = 0,
        sy = 0;
      if (e instanceof TouchEvent) {
        const t = e.touches[0] || e.changedTouches[0];
        if (!t) return;
        sx = t.clientX - rect.left;
        sy = t.clientY - rect.top;
      } else {
        const m = e as MouseEvent;
        sx = m.clientX - rect.left;
        sy = m.clientY - rect.top;
      }
      const p = screenToLocal(sx, sy);
      mouseRef.current.x = p.x;
      mouseRef.current.y = p.y;
      mouseRef.current.active = true;
    };
    const onLeave = () => (mouseRef.current.active = false);

    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('touchstart', onMove, { passive: true });
    canvas.addEventListener('touchmove', onMove, { passive: true });
    canvas.addEventListener('touchend', onLeave);
    canvas.addEventListener('touchcancel', onLeave);

    // Loop
    let t0 = performance.now();
    const loop = (now: number): void => {
      const dt = clamp((now - t0) / 16.6667, 0.5, 2);
      t0 = now;

      const W = cssW();
      const H = cssH();
      const amplitude = Math.min(W, H) * amplitudeBase;
      const twist = prefersReducedMotion ? 0 : now * 0.0007;

      const repelRadius =
        Math.min(W, H) * baseRepelRadiusFactor * breakIntensity;
      const repelStrength = baseRepelStrength * breakIntensity;

      // Update base positions (LOCAL space)
      for (let i = 0; i < dots.length; i++) {
        const d = dots[i];
        const t = d.tFrac;
        const x = t * W;
        const theta = t * turns * Math.PI * 2 + twist;
        const yCenter = H * 0.5;

        const yA = yCenter + amplitude * Math.sin(theta);
        const yB = yCenter + amplitude * Math.sin(theta + Math.PI);
        const depthA = Math.cos(theta);
        const depthB = Math.cos(theta + Math.PI);

        d.bx = x;
        if (d.strand === 0) {
          d.by = yA;
          d.depth = depthA;
        } else if (d.strand === 1) {
          d.by = yB;
          d.depth = depthB;
        } else {
          const tau = d.tau || 0.5;
          d.by = yA * (1 - tau) + yB * tau;
          d.depth = depthA * (1 - tau) + depthB * tau;
        }
      }

      // Physics in LOCAL space
      const m = mouseRef.current;
      for (let i = 0; i < dots.length; i++) {
        const d = dots[i];
        d.vx += (d.bx - d.x) * springK * dt;
        d.vy += (d.by - d.y) * springK * dt;

        if (m.active) {
          const dx = d.x - m.x;
          const dy = d.y - m.y;
          const dist = Math.hypot(dx, dy);
          if (dist < repelRadius) {
            const s = 1 - dist / repelRadius;
            const strength = repelStrength * (s * s);
            const nx = dx / (dist || 1);
            const ny = dy / (dist || 1);
            d.vx += nx * strength * 8 * dt;
            d.vy += ny * strength * 8 * dt;
          }
        }

        d.vx *= Math.pow(damping, dt);
        d.vy *= Math.pow(damping, dt);
        d.x += d.vx * dt;
        d.y += d.vy * dt;
      }

      // Move bokeh in LOCAL space
      if (bokehEnabled && bokeh.length) {
        const parallaxX = (m.active ? m.x - W * 0.5 : 0) * 0.002;
        const parallaxY = (m.active ? m.y - H * 0.5 : 0) * 0.002;
        for (let i = 0; i < bokeh.length; i++) {
          const p = bokeh[i];
          p.x += (p.vx + parallaxX * p.z) * dt;
          p.y += (p.vy + parallaxY * p.z) * dt;
          if (p.x < -10) p.x = W + 10;
          if (p.x > W + 10) p.x = -10;
          if (p.y < -10) p.y = H + 10;
          if (p.y > H + 10) p.y = -10;
        }
      }

      // ---------- render ----------
      const Wc = cssW();
      const Hc = cssH();

      // clear/fill
      if (background === 'transparent') ctx.clearRect(0, 0, Wc, Hc);
      else {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, Wc, Hc);
      }

      ctx.save();

      // DRAW order: translate in screen space, then rotate around canvas center
      const dxScreen = Wc * (posFrac(position) - 0.5);
      // Adjust offset based on position: for 'left' shift right, for 'right' shift left
      const horizontalOffset = position === 'left' 
        ? Wc * 0.15  // Shift right for left position
        : position === 'right'
        ? -Wc * 0.15 // Shift left for right position
        : 0;          // No offset for center
      ctx.translate(dxScreen + horizontalOffset, 0);

      const cx = Wc * 0.5,
        cy = Hc * 0.5;
      ctx.translate(cx, cy);
      ctx.rotate(angle);
      ctx.translate(-cx, -cy);

      // Bokeh
      if (bokehEnabled && bokeh.length) {
        ctx.save();
        for (const p of bokeh) {
          ctx.beginPath();
          ctx.globalAlpha = 0.18 + p.z * 0.22;
          ctx.shadowBlur = (8 + p.z * 18) * (Wc / 1000);
          ctx.shadowColor = bokehColor;
          ctx.fillStyle = bokehColor;
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      // DNA dots (depth-sorted)
      const order = dots.slice().sort((p, q) => p.depth - q.depth);
      for (const d of order) {
        const baseSize = 2 + ((d.depth + 1) / 2) * dotSize;
        const size =
          (d.strand === 2 ? baseSize * rungDotScale : baseSize) * (Wc / 1000);
        const alpha = 0.4 + ((d.depth + 1) / 2) * 0.6;

        ctx.beginPath();
        ctx.arc(d.x, d.y, size, 0, Math.PI * 2);

        ctx.fillStyle =
          d.strand === 0
            ? dotColorA
            : d.strand === 1
            ? dotColorB
            : rungColor || '#CED5EA';
        ctx.globalAlpha = alpha;
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      ctx.restore();

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    return (): void => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', handleResize);
      if (ro) {
        try {
          ro.unobserve(wrapper);
          ro.disconnect();
        } catch {
          /* noop */
        }
      }
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('touchstart', onMove);
      canvas.removeEventListener('touchmove', onMove);
      canvas.removeEventListener('touchend', onLeave);
      canvas.removeEventListener('touchcancel', onLeave);
    };
  }, [
    background,
    dotColorA,
    dotColorB,
    rungColor,
    height,
    rungs,
    width,
    breakIntensity,
    springKProp,
    dampingProp,
    aspectRatio,
    bokehEnabled,
    bokehCount,
    bokehColor,
    rungDotCount,
    rungDotScale,
    rotationDeg,
    position,
    amplitudeBase,
    turns,
    dotSize,
    prefersReducedMotion,
  ]);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        width: width ? width : '100%',
        height: height ? height : '100%',
        overflow: 'hidden',
        background,
      }}
    >
      <canvas
        ref={canvasRef}
        aria-label="Interactive DNA helix visualization"
      />
    </div>
  );
};

export default InteractiveDNA;
